import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PersonDTO, ProjectMemberDTO } from '@harness/shared';
import { PageHeader, Spinner } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { qk } from '../../lib/keys';
import type { ProjectDetail } from '../../lib/queries';
import { toast } from '../../lib/store';

// Project settings › Sharing. Projects are private to their creator, who can invite colleagues as
// viewers (read only) or editors (chat, run agents, approve). Invited people can leave.

export default function SharingPage({ project }: { project: ProjectDetail }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const key = ['project-members', project.id];
  const members = useQuery({ queryKey: key, queryFn: () => api.get<ProjectMemberDTO[]>(`/api/projects/${project.id}/members`) });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'viewer' | 'editor'>('editor');
  const people = useQuery({ queryKey: ['people', email], queryFn: () => api.get<PersonDTO[]>(`/api/people?q=${encodeURIComponent(email)}`), enabled: project.role === 'owner' });
  const isCreator = project.role === 'owner';

  const run = async (fn: () => Promise<ProjectMemberDTO[] | { ok: boolean }>, done: string) => {
    try {
      const r = await fn();
      if (Array.isArray(r)) qc.setQueryData(key, r);
      toast(done);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  const taken = new Set((members.data ?? []).map((m) => m.email));

  return (
    <div className="settings-content narrow">
      <PageHeader title="Sharing" sub="Only you and the people you invite can see this project. Admins can't see it." />
      {isCreator && (
        <section className="card card-pad stack-sm">
          <div className="section-title">Invite a colleague</div>
          <div className="row" style={{ flexWrap: 'nowrap', gap: 8 }}>
            <input className="input" list="share-people" placeholder="Name or email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Colleague's email" />
            <datalist id="share-people">
              {(people.data ?? [])
                .filter((p) => !taken.has(p.email))
                .map((p) => (
                  <option key={p.id} value={p.email}>
                    {p.name}
                  </option>
                ))}
            </datalist>
            <select className="input" style={{ width: 130 }} value={role} onChange={(e) => setRole(e.target.value as 'viewer' | 'editor')} aria-label="Access">
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
            <button
              className="btn btn-primary"
              disabled={!email.includes('@')}
              onClick={() => void run(() => api.post(`/api/projects/${project.id}/members`, { email: email.trim(), role }), `Invited as ${role}`).then(() => setEmail(''))}
            >
              <UserPlus className="icon" />
              Invite
            </button>
          </div>
          <div className="hint">Editors can chat, run agents and approve; viewers can read. Only you can invite people or delete the project.</div>
        </section>
      )}
      <section className="card card-pad stack-sm">
        <div className="section-title">People with access</div>
        {members.isLoading ? (
          <Spinner />
        ) : (
          <table className="table">
            <tbody>
              {(members.data ?? []).map((m) => (
                <tr key={m.userId}>
                  <td>
                    <b>{m.name}</b>
                    {m.you && <span className="tag tag-accent sm" style={{ marginLeft: 8 }}>You</span>}
                    <div className="muted" style={{ fontSize: 12 }}>
                      {m.email}
                    </div>
                  </td>
                  <td style={{ width: 150 }}>
                    {m.creator ? (
                      'Creator'
                    ) : isCreator ? (
                      <select
                        className="input"
                        value={m.role}
                        aria-label={`Access for ${m.name}`}
                        onChange={(e) => void run(() => api.patch(`/api/projects/${project.id}/members/${m.userId}`, { role: e.target.value }), `${m.name} is now ${e.target.value === 'editor' ? 'an editor' : 'a viewer'}`)}
                      >
                        <option value="editor">Editor</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    ) : m.role === 'editor' ? (
                      'Editor'
                    ) : (
                      'Viewer'
                    )}
                  </td>
                  <td style={{ width: 100, textAlign: 'right' }}>
                    {!m.creator && isCreator && (
                      <button className="btn btn-ghost" onClick={() => void run(() => api.del(`/api/projects/${project.id}/members/${m.userId}`), `${m.name} removed`)}>
                        Remove
                      </button>
                    )}
                    {!m.creator && m.you && (
                      <button
                        className="btn btn-ghost"
                        onClick={() =>
                          void run(() => api.del(`/api/projects/${project.id}/members/${m.userId}`), `You left ${project.name}`).then(async () => {
                            await qc.invalidateQueries({ queryKey: qk.projects });
                            nav('/');
                          })
                        }
                      >
                        Leave
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
